import React, { useEffect } from 'react';
import {   InfoBox,   ExerciseBox,   CodeBlock, CodeEditor, BoldTextParagraph,   InlineCode,   SectionTitle,  Note,  TableContainer,  Highlight,  PatternContainer,  SectionHeading,  SubHeading,  TwoColGrid,  ThreeColGrid,  GridItem,  Divider,  MutedText, Separator, CorrectionBox} from '../../../../components/course/CourseComponents';

const Chapitre3Content = ({ courseId = 'baseprogrammation' }) => {
  
  // Effet pour donner le focus à l'éditeur quand le composant se monte
  useEffect(() => {
    // Le focus sera géré par la prop autoFocus du CodeEditor
  }, []);

  return (
    <>
     <InfoBox title="Objectif" color="blue">
        <p className="mb-0">
        Apprendre à stocker et manipuler des informations avec les variables.
        </p>
      </InfoBox>       

      <Separator type="text" text="Qu'est-ce qu'une variable ?" color="gray-darker" size="md" />
      <SectionTitle>Qu'est-ce qu'une variable ?</SectionTitle>
      <p>
        Une variable est comme une boîte dans laquelle tu ranges une information : un prénom, un âge, un score, etc. 
        Tu lui donnes un nom, et tu peux l'utiliser à tout moment dans ton programme.
      </p>

      <Note type="gray" title="🔧 Exemple de création d'une variable :">
        <CodeBlock language="javascript">
          let prenom = "Michael";
        </CodeBlock>
        <ul className="list-disc list-inside mt-2">
          <li><InlineCode>let</InlineCode> : indique qu'on crée une variable</li>
          <li><InlineCode>prenom</InlineCode> : c'est le nom de la boîte</li>
          <li><InlineCode>"Michael"</InlineCode> : c'est ce qu'on met dedans</li>
        </ul>
      </Note>

      <p>
        Pour afficher le contenu d'une variable, on utilise son nom sans guillemets :
      </p>

      <Note type="gray" title="🔧 Pour afficher la variable :">
        <CodeBlock language="javascript">
          print(prenom);
        </CodeBlock>
      </Note>

      <Note type="exercice" title="🧪 À toi de jouer :">
        Crée une variable <InlineCode>prenom</InlineCode> avec ton prénom, puis affiche-la dans la console :<br/>
        - Utilise le mot-clé <InlineCode>let</InlineCode> et donne un nom à ta variable<br/>
        - Utilise le signe = pour assigner une valeur à ta vairable, puis mets ton prénom entre guillemets<br/>
        - Affiche la variable avec <InlineCode>print()</InlineCode>
        
        <CorrectionBox>
          <CodeBlock language="javascript">
{`let prenom = "TonPrénom";
print(prenom);`}
          </CodeBlock>
        </CorrectionBox>
      </Note>

      <CodeEditor
        courseId={courseId}
        editorId="variables-base"
        title="Créer et afficher une variable"
        showConsole={true}
        height="200px"
        initialCode=""
        autoFocus={true}
      />

      <Note type="info" title="⚠️ Attention :">
        <CodeBlock language="javascript">
{`print("prenom"); // affiche le mot prenom (en texte)
print(prenom);   // affiche la valeur de la variable`}
        </CodeBlock>
      </Note>

      <Separator type="text" text="Types de variables" color="gray-darker" size="md" />
      <SectionTitle>Types de variables</SectionTitle>
      <p>
        Voici différents types de données que tu peux ranger dans des variables :
      </p>

      <Note type="gray" title="🎒 Types de données :">
        <CodeBlock language="javascript">
{`let nom = "Léo";        // chaîne de caractères (texte)
let age = 12;           // nombre
let estAdulte = false;  // booléen (vrai ou faux)`}
        </CodeBlock>
      </Note>

      <Separator type="text" text="Modifier une variable" color="gray-darker" size="md" />
      <SectionTitle>Modifier une variable</SectionTitle>
      <p>
        Tu peux changer la valeur d'une variable après l'avoir créée :
      </p>

      <Note type="gray" title="🔁 Modification d'une variable :">
        <CodeBlock language="javascript">
{`let score = 10;
score = 20;
print(score); // affiche 20`}
        </CodeBlock>
      </Note>


      <Separator type="text" text="Opérations avec les variables" color="gray-darker" size="md" />
      <SectionTitle>Opérations avec les variables</SectionTitle>
      
      <SubHeading>Addition de nombres</SubHeading>
      <Note type="gray" title="➕ Addition :">
        <CodeBlock language="javascript">
{`let a = 5;
let b = 3;
let total = a + b;
print(total); // affiche 8`}
        </CodeBlock>
      </Note>


      <Note type="exercice" title="🧪 Exercice multiplication :">
        Calcule et affiche le résultat de la multiplication de deux nombres :<br/>
        - Crée deux variables avec des nombres de ton choix<br/>
        - Multiplie-les avec l'opérateur <InlineCode>*</InlineCode><br/>
        - Affiche le résultat avec <InlineCode>print()</InlineCode>
        
        <CorrectionBox>
          <CodeBlock language="javascript">
{`let nombre1 = 7;
let nombre2 = 8;
let resultat = nombre1 * nombre2;
print(resultat); // affiche 56`}
          </CodeBlock>
        </CorrectionBox>
      </Note>

      <CodeEditor
        courseId={courseId}
        editorId="variables-exercice1"
        title="🧪 Exercice : Multiplication de nombres"
        showConsole={true}
        height="250px"
        initialCode=""
      />

<Separator type="text" text="Concaténer (coller) du texte" color="gray-darker" size="md" />
      <SubHeading>Concaténer (coller) du texte</SubHeading>
      <Note type="gray" title="🔗 Concaténation :">
        <CodeBlock language="javascript">
{`let nom = "Léo";
let message = "Salut, " + nom + " !";
print(message); // affiche "Salut, Léo !"`}
        </CodeBlock>
      </Note>

      <Note type="exercice" title="🧪 Exercice concaténation :">
        Affiche ce message dans la console : <InlineCode>"Salut, je m'appelle Michael et j'ai 40 ans."</InlineCode><br/>
        En utilisant des variables :<br/>
        - Crée une variable <InlineCode>prenom</InlineCode> avec ton prenom<br/>
        - Crée une variable <InlineCode>age</InlineCode> avec ton age<br/>
        - Utilise l'opérateur <InlineCode>+</InlineCode> pour coller les textes<br/>
        
        <CorrectionBox>
          <CodeBlock language="javascript">
{`let prenom = "Michael";
let age = 40;
let texte = "Salut, je m'appelle " + prenom + " et j'ai " + age + " ans.";
print(texte);`}
          </CodeBlock>
        </CorrectionBox>
      </Note>

      <CodeEditor
        courseId={courseId}
        editorId="variables-exercice2"
        title="Exercice : Variables et concaténation"
        showConsole={true}
        height="250px"
        initialCode=""
      />

<Separator type="text" text="Portée des variables" color="gray-darker" size="md" />
      <SubHeading>Portée des variables</SubHeading>



      <p>
        La portée d'une variable définit où tu peux l'utiliser dans le code.
      </p>

      <Note type="gray" title="🎯 Les différents types de portée :">
        <ul className="list-disc list-inside space-y-2">
          <br/>
          <li><strong>Variable globale :</strong> une variable déclarée en dehors de toute fonction est accessible partout !</li>
          <li><strong>Variable dans une fonction :</strong> une variable déclarée dans une fonction ne peut être utilisée que dans celle-ci.</li>
          <li><strong>Variable dans un bloc :</strong> une variable déclarée dans un bloc (<InlineCode>if {`{}`}</InlineCode>, <InlineCode>for {`{}`}</InlineCode>, <InlineCode>while {`{}`}</InlineCode>…) ne peut être utilisée que dans ce bloc.</li>
        </ul>
      </Note>

      <Note type="tip" title="💡 En résumé : ">
        <p className="mb-0">
          Si tu veux une variable que tu puisses utiliser dans tout ton code (pour les variables de configuration par exemple), tu la déclares tout en haut de ton code.
          Si tu souhaites l'utiliser temporairement, tu la déclares dans ta fonction ou dans ton bloc.
        </p>
      </Note>

      <p>
        ✅ Bravo ! Maintenant tu sais créer, modifier et utiliser des variables.<br/>
        
      </p>
      
      <BoldTextParagraph>
        Prochaine étape : les conditions !
      </BoldTextParagraph>

  </>
  );
};

export default Chapitre3Content; 